import * as React from "react";
import { useContext } from "react";
import { useAppTabList } from "@1js/acui-tablist";
import { dataUniqueId } from "@1js/acui-common";
import { useEventCallback, mergeCallbacks } from "@fluentui/react-utilities";
import type { SelectTabEventHandler } from "@fluentui/react-components";
import { mergeClasses } from "@fluentui/react-components";
import type { TabListProps, TabListState } from "./TabList.types";
import type { AppTabListOverflowMenuProps } from "@1js/acui-tablist";
import { useCommonArrowNavGroup } from "@1js/acui-focus";
import { AppContext } from "@1js/acui-context";
import { useOnKeyDown } from "./useOnKeyDown";
import { InputMethod } from "@1js/acui-common";
import type { PivotHeaderExecutePayload } from "@1js/acui-pivot";

const noOpFileMenuOpenId = "noOpFileMenuOpenId";

export const useTablist = (
  props: TabListProps,
  ref: React.ForwardedRef<HTMLElement>
): TabListState => {
  const {
    onOverflowButtonExecute,
    onTabHeaderClick,
    overflowMenuLauncherLabel,
    overflowTabHeaderMenuLauncherIcon,
    overflowTabHeaders,
    topLevelTabHeaders,
    useNormalStyleForContextualTabs,
    tabListOverflowMenu,
    hideSelectedTabIndicators,
    keytipProps,
    selectedTabId,
    focusTabListRef,
  } = props;

  const [localSelectedTabId, setSelectedTabId] = React.useState(selectedTabId);

  const [focusOnOverflow, setFocusOnOverflow] = React.useState(false);

  // Ensure the selectedTab from props is set as the localSelectedTab
  // in the case where a contextual tab is added/removed and set elsewhere
  React.useEffect(() => {
    setSelectedTabId(selectedTabId);
  }, [selectedTabId, setSelectedTabId]);

  const onTabSelect: SelectTabEventHandler = React.useCallback(
    (_event, data) => {
      onTabHeaderClick(data.value as string, {
        inputMethod: InputMethod.Keyboard,
      });
      setSelectedTabId(data.value as string);
    },
    [onTabHeaderClick, setSelectedTabId]
  );

  React.useImperativeHandle(
    focusTabListRef,
    () => ({
      focus: () => {
        setFocusOnOverflow(false);
        props?.onFocus?.();
      },
    }),
    [setFocusOnOverflow, props]
  );

  const onClick = React.useCallback(
    (tabId: string, data?: PivotHeaderExecutePayload) => {
      onTabHeaderClick(tabId, data);
      setSelectedTabId(tabId);
    },
    [onTabHeaderClick, setSelectedTabId]
  );

  const tabs = topLevelTabHeaders.map((header) => ({
    ...header,
    "data-unique-id": dataUniqueId(header),
    key: header.id,
    value: header.id,
    onTabHeaderExecute: onClick,
    useNormalStyleForContextualTabs,
    children: header.title,
    hideSelectedIndicator: hideSelectedTabIndicators,
    title: header.title || "",
  }));

  const context = useContext(AppContext);

  const tabListOverflowMenuState: AppTabListOverflowMenuProps = {
    ariaLabel: overflowMenuLauncherLabel,
    ...(tabListOverflowMenu as AppTabListOverflowMenuProps),
    overflowTabHeaderMenuLauncherIcon,
    topItems: topLevelTabHeaders,
    overflowItems: overflowTabHeaders || [],
    keytipProps,
    onOverflowButtonExecute,
    menuListWrapper: { className: mergeClasses(context.responsiveClassName) },
  };

  tabListOverflowMenuState.onMenuButtonClick = useEventCallback(
    mergeCallbacks(tabListOverflowMenuState.onMenuButtonClick, () =>
      onOverflowButtonExecute?.({ id: noOpFileMenuOpenId })
    )
  );
  tabListOverflowMenuState.onMenuItemClick = useEventCallback(
    mergeCallbacks(tabListOverflowMenuState.onMenuItemClick, (event, id) =>
      onTabSelect(event, { value: id })
    )
  );

  // Controls the arrow nav for the tablist and ensures
  // the tab event key is not handled so the ribbon container
  // can handle the tab loop
  const focusAttributes = useCommonArrowNavGroup(true /** disableTab */);

  const tabListOnKeyDown = useOnKeyDown(
    props,
    selectedTabId,
    setSelectedTabId,
    setFocusOnOverflow,
    onTabSelect,
    focusOnOverflow
  );

  const tabListState = useAppTabList(
    {
      ...props,
      id: "tablist",
      tabIndex: -1,
      appearance: "subtle",
      onTabSelect,
      onTabHeaderClick: onClick,
      selectedTabId: localSelectedTabId,
      onKeyDown: tabListOnKeyDown,
      ...focusAttributes,
    },
    ref
  );

  return {
    ...props,
    ...tabListState,
    tabs,
    tabListOverflowMenu: tabListOverflowMenuState,
  } as TabListState;
};
